'use client'

import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@planner/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { useMenuItems, useMenuPackages } from '@/features/menu/hooks/useMenu'
import { useMenuCategories } from '@/features/menu/hooks/useMenuCategories'
import { useCompanySettings } from '@/features/contracts/hooks/useCompanySettings'
import { EventServicesConfigurator } from '@/features/services/components/EventServicesConfigurator'
import type { MenuItem, EventMenuItem, Event, MenuPackage, MenuCategory } from '@planner/types'
import { X } from 'lucide-react'
import { useRouter } from 'next/navigation'

interface MenuCustomConfiguratorProps {
  eventId: string
  event: Pick<Event, 'id' | 'number_of_guests' | 'menu_package_id'>
  onClose: () => void
  wizardMode?: boolean
  onConfigured?: (items: Array<{
    menu_item_id: string
    quantity_percentage: number
  }>) => void
  initialItems?: Array<{
    menu_item_id: string
    quantity_percentage: number
  }>
}

export function MenuCustomConfigurator({ 
  eventId, 
  event, 
  onClose,
  wizardMode = false,
  onConfigured,
  initialItems,
}: MenuCustomConfiguratorProps) {
  const router = useRouter()
  const { items: menuItems, loading: itemsLoading } = useMenuItems()
  const { packages: menuPackages } = useMenuPackages()
  const { settings: companySettings } = useCompanySettings()
  const { categories: menuCategories } = useMenuCategories()
  const [selectedItems, setSelectedItems] = useState<Array<{
    menu_item_id: string
    quantity_percentage: number
  }>>(initialItems || [])
  const [loading, setLoading] = useState(false)
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false)

  useEffect(() => {
    // If initialItems provided, use them and skip loading
    if (initialItems && initialItems.length > 0 && !hasLoadedInitial) {
      setSelectedItems(initialItems)
      setHasLoadedInitial(true)
      return
    }

    // Otherwise load from database or sessionStorage
    if (!hasLoadedInitial) {
      loadEventItems()
      setHasLoadedInitial(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, initialItems])

  const loadEventItems = async () => {
    try {
      // First check if there are items in sessionStorage (from package conversion)
      const sessionKey = `customMenuItems_${eventId}`
      const sessionData = sessionStorage.getItem(sessionKey)
      
      if (sessionData) {
        try {
          const items = JSON.parse(sessionData)
          setSelectedItems(items)
          // Clear sessionStorage after loading
          sessionStorage.removeItem(sessionKey)
          return
        } catch (e) {
          console.error('Error parsing sessionStorage data:', e)
        }
      }

      // Otherwise load from database
      const { data, error } = await supabase
        .from('event_menu_items')
        .select('*')
        .eq('event_id', eventId)

      if (error) throw error
      setSelectedItems(
        (data || []).map((item: EventMenuItem) => ({
          menu_item_id: item.menu_item_id,
          quantity_percentage: item.quantity_percentage,
        }))
      )
    } catch (error) {
      console.error('Error loading event items:', error)
    }
  }

  const addMenuItem = () => {
    setSelectedItems([...selectedItems, { menu_item_id: '', quantity_percentage: 100 }])
  }

  const updateMenuItem = (index: number, field: string, value: string | number) => {
    const updated = [...selectedItems]
    updated[index] = { ...updated[index], [field]: value }
    setSelectedItems(updated)
  }

  const removeMenuItem = (index: number) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index))
  }

  const toggleItemSelection = (itemId: string) => {
    const existingIndex = selectedItems.findIndex((item) => item.menu_item_id === itemId)
    
    if (existingIndex >= 0) {
      // Remove item
      setSelectedItems(selectedItems.filter((item) => item.menu_item_id !== itemId))
    } else {
      // Add item with default 100%
      setSelectedItems([
        ...selectedItems,
        {
          menu_item_id: itemId,
          quantity_percentage: 100,
        },
      ])
    }
  }

  const updateItemPercentage = (itemId: string, percentage: number) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.menu_item_id === itemId
          ? { ...item, quantity_percentage: percentage }
          : item
      )
    )
  }

  const calculateItemTotal = (item: { menu_item_id: string; quantity_percentage: number }) => {
    const menuItem = menuItems.find((m: MenuItem) => m.id === item.menu_item_id)
    if (!menuItem) return 0
    const quantity = Math.ceil((item.quantity_percentage / 100) * event.number_of_guests)
    return menuItem.price * quantity
  }

  const calculateCustomMenuFee = () => {
    // Get fee from global company settings
    if (!companySettings?.custom_menu_fee_enabled || !companySettings.custom_menu_fee_amount) {
      return 0
    }
    if (companySettings.custom_menu_fee_type === 'per_person') {
      return companySettings.custom_menu_fee_amount * event.number_of_guests
    }
    return companySettings.custom_menu_fee_amount
  }

  const calculateTotal = () => {
    const itemsTotal = selectedItems.reduce((sum, item) => sum + calculateItemTotal(item), 0)
    const fee = calculateCustomMenuFee()
    return itemsTotal + fee
  }

  const handleSave = async () => {
    if (wizardMode && onConfigured) {
      // In wizard mode, just call the callback
      onConfigured(selectedItems.filter((item) => item.menu_item_id))
      return
    }

    setLoading(true)
    try {
      // Delete existing items
      await supabase.from('event_menu_items').delete().eq('event_id', eventId)

      // Insert new items
      const itemsToInsert = selectedItems
        .filter((item) => item.menu_item_id)
        .map((item) => ({
          event_id: eventId,
          menu_item_id: item.menu_item_id,
          quantity_percentage: item.quantity_percentage,
        }))

      if (itemsToInsert.length > 0) {
        const { error } = await supabase.from('event_menu_items').insert(itemsToInsert as unknown as never)
        if (error) throw error
      }

      // Clear package selection for custom menu
      await supabase
        .from('events')
        .update({ menu_package_id: null } as unknown as never)
        .eq('id', eventId)

      onClose()
      router.refresh()
    } catch (error) {
      console.error('Error saving menu:', error)
      alert('Błąd podczas zapisywania menu')
    } finally {
      setLoading(false)
    }
  }

  // In wizard mode, call onConfigured whenever selectedItems change
  useEffect(() => {
    if (wizardMode && onConfigured) {
      onConfigured(selectedItems.filter((item) => item.menu_item_id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, wizardMode])

  // Grupuj dania po kategoriach - dla trybu wizard używamy kategorii menu
  const getItemsByCategory = () => {
    if (wizardMode && menuCategories.length > 0) {
      // W trybie wizard grupuj po kategoriach menu (zachowując kolejność)
      const itemsByCat: Record<string, MenuItem[]> = {}
      
      // Sortuj kategorie według display_order
      const sortedCategories = [...menuCategories].sort((a, b) => 
        (a.display_order || 0) - (b.display_order || 0)
      )
      
      sortedCategories.forEach((category: MenuCategory) => {
        const itemsInCategory = menuItems.filter((item: MenuItem) => {
          if (item.categories && item.categories.length > 0) {
            return item.categories.some(cat => 
              (cat as MenuCategory).id === category.id || 
              (cat as MenuCategory).name === category.name
            )
          }
          return false
        })
        
        if (itemsInCategory.length > 0) {
          itemsByCat[category.name] = itemsInCategory
        }
      })
      
      // Dodaj dania bez kategorii
      const itemsWithoutCategory = menuItems.filter((item: MenuItem) => {
        if (item.categories && item.categories.length > 0) {
          // Sprawdź czy wszystkie kategorie są w menuCategories
          return !item.categories.some(cat => {
            const catId = (cat as MenuCategory).id
            const catName = (cat as MenuCategory).name
            return menuCategories.some(mc => mc.id === catId || mc.name === catName)
          })
        }
        return true
      })
      
      if (itemsWithoutCategory.length > 0) {
        itemsByCat['Inne'] = itemsWithoutCategory
      }
      
      return itemsByCat
    }
    
    // Dla trybu normalnego - stara logika
    return menuItems.reduce((acc: Record<string, MenuItem[]>, item: MenuItem) => {
      const itemCategories = item.categories && item.categories.length > 0
        ? item.categories.map(cat => (cat as MenuCategory).name || (cat as any).name || 'Inne')
        : (item.category ? [item.category] : ['Inne'])
      
      itemCategories.forEach((catName: string) => {
        if (!acc[catName]) {
          acc[catName] = []
        }
        if (!acc[catName].some(i => i.id === item.id)) {
          acc[catName].push(item)
        }
      })
      
      return acc
    }, {})
  }

  const itemsByCategory = getItemsByCategory()

  if (itemsLoading) {
    return <div className="p-8">Ładowanie...</div>
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Menu niestandardowe</CardTitle>
            {!wizardMode && (
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {wizardMode ? (
            // Tryb wizard - widok z checkboxami
            <div className="space-y-6">
              {Object.entries(itemsByCategory).map(([categoryName, categoryItems]) => (
                <div key={categoryName} className="space-y-3">
                  <h3 className="font-semibold text-lg">{categoryName}</h3>
                  <div className="space-y-3">
                    {categoryItems.map((menuItem: MenuItem) => {
                      const isSelected = selectedItems.some((item) => item.menu_item_id === menuItem.id)
                      const selectedItem = selectedItems.find((item) => item.menu_item_id === menuItem.id)
                      const quantity = selectedItem
                        ? Math.ceil((selectedItem.quantity_percentage / 100) * event.number_of_guests)
                        : 0
                      const itemTotal = menuItem.price * quantity

                      return (
                        <div
                          key={menuItem.id}
                          className={`p-4 border rounded-lg ${
                            isSelected ? 'bg-primary/5 border-primary' : ''
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex items-center pt-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleItemSelection(menuItem.id)}
                                className="w-4 h-4"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-medium">{menuItem.name}</div>
                                  {menuItem.description && (
                                    <div className="text-sm text-muted-foreground mt-1">
                                      {menuItem.description}
                                    </div>
                                  )}
                                  <div className="text-sm font-medium mt-2">
                                    {menuItem.price.toFixed(2)} zł / osoba
                                  </div>
                                </div>
                              </div>

                              {isSelected && (
                                <div className="mt-4 space-y-2">
                                  <Label htmlFor={`percentage-${menuItem.id}`}>
                                    Dla ilu % gości? ({quantity} porcji)
                                  </Label>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      id={`percentage-${menuItem.id}`}
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={selectedItem?.quantity_percentage || 100}
                                      onChange={(e) =>
                                        updateItemPercentage(menuItem.id, parseInt(e.target.value) || 0)
                                      }
                                      className="w-24"
                                    />
                                    <span className="text-sm text-muted-foreground">%</span>
                                    <div className="flex-1 text-right font-medium">
                                      Razem: {itemTotal.toFixed(2)} zł
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Tryb normalny - widok z dropdownami
            <>
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Dania</h3>
                <Button type="button" onClick={addMenuItem} size="sm">
                  Dodaj danie
                </Button>
              </div>

              {selectedItems.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Brak dań. Dodaj pierwsze danie.
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedItems.map((item, index) => {
                    const menuItem = menuItems.find((m: MenuItem) => m.id === item.menu_item_id)
                    return (
                      <div key={index} className="p-4 border rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="text-sm font-medium mb-1 block">Danie</label>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              value={item.menu_item_id}
                              onChange={(e) => updateMenuItem(index, 'menu_item_id', e.target.value)}
                            >
                              <option value="">Wybierz danie</option>
                              {Object.entries(itemsByCategory).map(([category, items]) => (
                                <optgroup key={category} label={category}>
                                  {items.map((m: MenuItem) => (
                                    <option key={m.id} value={m.id}>
                                      {m.name} - {m.price.toFixed(2)} zł
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">
                              Ilość (% gości)
                            </label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={item.quantity_percentage}
                              onChange={(e) =>
                                updateMenuItem(index, 'quantity_percentage', parseInt(e.target.value) || 0)
                              }
                            />
                            {menuItem && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {Math.ceil((item.quantity_percentage / 100) * event.number_of_guests)} porcji
                              </div>
                            )}
                          </div>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <div className="text-sm text-muted-foreground">Cena</div>
                              <div className="text-lg font-bold">
                                {calculateItemTotal(item).toFixed(2)} zł
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              onClick={() => removeMenuItem(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}

          <div className="pt-4 border-t space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium">Cena dań:</span>
              <span className="text-lg font-bold">
                {selectedItems.reduce((sum, item) => sum + calculateItemTotal(item), 0).toFixed(2)} zł
              </span>
            </div>
            {calculateCustomMenuFee() > 0 && (
              <>
                <div className="flex justify-between items-center text-sm text-muted-foreground">
                  <span>Opłata za menu niestandardowe:</span>
                  <span>{calculateCustomMenuFee().toFixed(2)} zł</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="text-lg font-medium">Cena całkowita:</span>
                  <span className="text-3xl font-bold">{calculateTotal().toFixed(2)} zł</span>
                </div>
              </>
            )}
            {calculateCustomMenuFee() === 0 && (
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium">Cena całkowita:</span>
                <span className="text-3xl font-bold">{calculateTotal().toFixed(2)} zł</span>
              </div>
            )}
          </div>

          {!wizardMode && (
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Anuluj
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? 'Zapisywanie...' : 'Zapisz'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {!wizardMode && (
        <EventServicesConfigurator
          eventId={eventId}
          numberOfGuests={event.number_of_guests}
        />
      )}
    </div>
  )
}

